import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import Multiselect from 'multiselect-react-dropdown';
import InputV1 from '../../../../../components/ComponentsV1/InputV1';
import { multiSelectStyles } from '../../ComplaintTypePolicy/constants';
import Button from '../../../../../components/ComponentsV1/Button';

const ATTRIBUTE_NAME = 'karma_score';
const OPERATOR_CONTAINS = 'contains';

const emptyRule = () => ({ attribute_name: ATTRIBUTE_NAME, operator: '', attribute_value: '' });

const RuleSection = ({ title, rule, onChange, operatorOptions, attributeValueOptions }) => {
  const isContains = rule.operator === OPERATOR_CONTAINS;

  // Transform to {id, name} shape for Multiselect
  const multiSelectOptions = attributeValueOptions.map((opt) => ({
    id: opt.value,
    name: String(opt.label),
  }));

  const handleOperatorChange = (opt) => {
    const newOperator = opt?.value ?? '';
    // Reset attribute_value when switching between contains and non-contains
    const wasContains = rule.operator === OPERATOR_CONTAINS;
    const willBeContains = newOperator === OPERATOR_CONTAINS;
    const resetValue = wasContains !== willBeContains;
    onChange({
      ...rule,
      operator: newOperator,
      attribute_value: resetValue ? (willBeContains ? [] : '') : rule.attribute_value,
    });
  };

  const handleMultiValueChange = (selectedList) => {
    onChange({ ...rule, attribute_value: selectedList || [] });
  };

  return (
    <div className='tw-flex tw-flex-col tw-gap-4'>
      <div className='tw-text-[1.4rem] tw-font-semibold tw-text-grey-900'>{title}</div>
      <div className='tw-flex tw-items-start tw-gap-4'>
        {/* attribute_name — always karma_score, readonly */}
        <div className='tw-flex tw-flex-col tw-gap-2 tw-flex-1'>
          <span className='tw-text-[1.2rem] tw-text-zinc-500 tw-font-medium'>Attribute</span>
          <div className='tw-px-4 tw-py-[0.6rem] tw-rounded-lg tw-border tw-border-solid tw-border-[#cfcfcf] tw-text-grey-800 tw-bg-grey-100 tw-text-[1.3rem] tw-h-[3.6rem] tw-flex tw-items-center'>
            {ATTRIBUTE_NAME}
          </div>
        </div>

        {/* operator dropdown */}
        <div className='tw-flex tw-flex-col tw-gap-2 tw-flex-1'>
          <span className='tw-text-[1.2rem] tw-text-zinc-500 tw-font-medium'>Operator</span>
          <InputV1
            options={operatorOptions}
            multiple={false}
            isSearchable
            closeOnSelect
            maxVisible={3}
            placeholder='Select operator'
            value={rule.operator || undefined}
            onSelect={handleOperatorChange}
            style={{ width: '100%' }}
          />
        </div>

        {/* attribute_value — Multiselect for contains, single InputV1 otherwise */}
        <div css={multiSelectStyles} className='tw-flex tw-flex-col tw-gap-2 tw-flex-1 tw-min-w-0'>
          <span className='tw-text-[1.2rem] tw-text-zinc-500 tw-font-medium'>Value</span>
          {isContains ? (
            <Multiselect
              avoidHighlightFirstOption
              options={multiSelectOptions}
              selectedValues={Array.isArray(rule.attribute_value) ? rule.attribute_value : []}
              onSelect={handleMultiValueChange}
              onRemove={handleMultiValueChange}
              displayValue='name'
              placeholder='Search values...'
              style={{ optionContainer: { maxHeight: '12rem', overflowY: 'auto' } }}
            />
          ) : (
            <InputV1
              key='value-single'
              options={attributeValueOptions}
              multiple={false}
              isSearchable
              closeOnSelect
              maxVisible={3}
              placeholder='Select value'
              value={
                rule.attribute_value !== '' && !Array.isArray(rule.attribute_value)
                  ? String(rule.attribute_value)
                  : undefined
              }
              onSelect={(opt) => onChange({ ...rule, attribute_value: opt?.value ?? '' })}
              style={{ width: '100%' }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const PickupPolicyForm = ({
  operators,
  attributeValues,
  initialPolicy,
  onSubmit,
  onCancel,
  loading,
}) => {
  const [mandateAlwaysRule, setMandateAlwaysRule] = useState(emptyRule());
  const [mandateNoPickupRule, setMandateNoPickupRule] = useState(emptyRule());

  useEffect(() => {
    const normalizeRule = (r) => {
      if (!r) return emptyRule();
      const isContains = r.operator === OPERATOR_CONTAINS;
      if (isContains) {
        const rawValue = Array.isArray(r.attribute_value) ? r.attribute_value : [];
        return {
          ...r,
          attribute_value: rawValue.map((v) =>
            typeof v === 'object' && v !== null && 'id' in v ? v : { id: v, name: String(v) }
          ),
        };
      }
      return { ...r, attribute_value: Array.isArray(r.attribute_value) ? '' : r.attribute_value };
    };
    setMandateAlwaysRule(normalizeRule(initialPolicy?.mandate_always_pickup?.rules?.[0]));
    setMandateNoPickupRule(normalizeRule(initialPolicy?.mandate_no_pickup?.rules?.[0]));
  }, [initialPolicy]);

  const operatorOptions = (operators || []).map((op) => ({
    label: op.label,
    value: op.value,
  }));

  const attributeValueOptions = (attributeValues || []).map((v) => ({
    label: String(v),
    value: v,
  }));

  const hasValue = (rule) => {
    if (Array.isArray(rule.attribute_value)) return rule.attribute_value.length > 0;
    return rule.attribute_value !== '';
  };

  const isRuleEmpty = (rule) => !rule.operator && !hasValue(rule);
  const isRuleValid = (rule) => !!rule.operator && hasValue(rule);

  const isRulePartial = (rule) => !isRuleEmpty(rule) && !isRuleValid(rule);

  const serializeRule = (rule) => {
    if (rule.operator === OPERATOR_CONTAINS && Array.isArray(rule.attribute_value)) {
      return { ...rule, attribute_value: rule.attribute_value.map((item) => item?.id ?? item) };
    }
    return rule;
  };

  const handleSubmit = () => {
    if (isRulePartial(mandateAlwaysRule)) {
      toast.error('Mandate Always Pickup: both operator and value are required');
      return;
    }
    if (isRulePartial(mandateNoPickupRule)) {
      toast.error('Mandate No Pickup: both operator and value are required');
      return;
    }

    const pickupPolicy = {
      mandate_always_pickup: {
        rules: isRuleValid(mandateAlwaysRule) ? [serializeRule(mandateAlwaysRule)] : [],
      },
      mandate_no_pickup: {
        rules: isRuleValid(mandateNoPickupRule) ? [serializeRule(mandateNoPickupRule)] : [],
      },
    };
    onSubmit(pickupPolicy);
  };

  return (
    <div className='tw-border tw-border-solid tw-border-[#e5e7eb] tw-rounded-lg tw-p-6 tw-flex tw-flex-col tw-gap-8'>
      <RuleSection
        title='Mandate Always Pickup'
        rule={mandateAlwaysRule}
        onChange={setMandateAlwaysRule}
        operatorOptions={operatorOptions}
        attributeValueOptions={attributeValueOptions}
      />

      <div className='tw-border-t tw-border-solid tw-border-[#e5e7eb] tw-border-x-0 tw-border-b-0' />

      <RuleSection
        title='Mandate No Pickup'
        rule={mandateNoPickupRule}
        onChange={setMandateNoPickupRule}
        operatorOptions={operatorOptions}
        attributeValueOptions={attributeValueOptions}
      />

      <div className='tw-flex tw-gap-4'>
        <Button title='Save' onClick={handleSubmit} loading={loading} />
        <Button title='Cancel' type='secondary' onClick={onCancel} />
      </div>
    </div>
  );
};

export default PickupPolicyForm;
